import PropTypes from "prop-types";
import classNames from "classnames";
import styles from "./DateRangePicker.module.css";
import chevron from "./chevron.png";
import { useState, useEffect } from "react";

/**
 * Interactive component, lets the user pick a date from a calendar
 */
export function DateRangePicker({
  startDateString,
  endDateString,
  setStartDate,
  setEndDate,
  allowPastDate,
  size,
  allowRangeSelection=true,
}) {
  const [localStartDate, setLocalStartDate] = useState(null);
  const [localEndDate, setLocalEndDate] = useState(null);

  const [currentMonth, setMonth] = useState(null); // current month the calendar is on
  const [currentYear, setYear] = useState(null);
  const [days, setDays] = useState([]);

  useEffect(() => {
    if (!startDateString) {
      setLocalStartDate(null);
      setLocalEndDate(null);
      var d = new Date();
      setMonth(d.getMonth());
      setYear(d.getUTCFullYear());
    } else {
      var sd = new Date(startDateString);
      var ed = new Date(endDateString ? endDateString : startDateString);
      setLocalStartDate(sd);
      setLocalEndDate(ed);
      setMonth(sd.getMonth());
      setYear(sd.getUTCFullYear());
    }
  }, []);

  useEffect(() => {
    if (localStartDate && localEndDate) {
      setStartDate(formatDate(localStartDate));
      setEndDate(formatDate(localEndDate));
    }
    if (currentMonth && currentYear) {
      setDays(getDays(currentMonth + 1, currentYear));
    }
  }, [localStartDate, localEndDate]);

  useEffect(() => {
    if (currentMonth != null && currentYear != null) {
      setDays(getDays(currentMonth + 1, currentYear));
    }
  }, [currentMonth, currentYear]);

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  function incrementMonth() {
    var nextMonthDate = new Date(currentMonth + 1 + "/01/" + currentYear);
    nextMonthDate.setMonth(nextMonthDate.getMonth() + 1);
    setMonth(nextMonthDate.getMonth());
    setYear(nextMonthDate.getUTCFullYear());
  }
  function decrementMonth() {
    var lastMonthDate = new Date(currentMonth + 1 + "/01/" + currentYear);
    lastMonthDate.setMonth(lastMonthDate.getMonth() - 1);
    setMonth(lastMonthDate.getMonth());
    setYear(lastMonthDate.getUTCFullYear());
  }

  function isDateBeforeToday(date) {
    if (allowPastDate !== true) {
      var todaysDate = new Date();
      if (date.getTime() < todaysDate.getTime()) {
        if (
          date.getYear() == todaysDate.getYear() &&
          date.getMonth() == todaysDate.getMonth() &&
          date.getDate() == todaysDate.getDate()
        ) {
          return false;
        }
        return true;
      }
      return false;
    }
  }

  function isDateBeforeDate(date, date2) {
    if (date.getTime() < date2.getTime()) {
      if (
        date.getYear() == date2.getYear() &&
        date.getMonth() == date2.getMonth() &&
        date.getDate() == date2.getDate()
      ) {
        return false;
      }
      return true;
    }
    return false;
  }

  function isDateAfterDate(date, date2) {
    if (date.getTime() > date2.getTime()) {
      if (
        date.getYear() == date2.getYear() &&
        date.getMonth() == date2.getMonth() &&
        date.getDate() == date2.getDate()
      ) {
        return false;
      }
      return true;
    }
    return false;
  }

  function getDays(month, year) {
    var sDate = new Date(month + "/01/" + year);

    if (sDate.getDay() != 1) {
      while (sDate.getDay() != 1) {
        // making sure we start on a monday
        sDate.setDate(sDate.getDate() - 1);
      }
    }
    var currentDate = sDate;

    var d = [];
    for (var i = 0; i < 42; i++) {
      d.push({
        day: currentDate.getDate(),
        month: currentDate.getMonth(),
        year: currentDate.getYear(),
        dateString: currentDate.toLocaleDateString(),
        grayedOut: currentDate.getMonth() != month - 1,
        disabled: isDateBeforeToday(currentDate),
        highlighted:
          localStartDate && localEndDate
            ? (currentDate.getMonth() == localStartDate.getMonth() &&
                currentDate.getDate() == localStartDate.getDate() &&
                currentDate.getYear() == localStartDate.getYear()) ||
              (currentDate.getMonth() == localEndDate.getMonth() &&
                currentDate.getDate() == localEndDate.getDate() &&
                currentDate.getYear() == localEndDate.getYear())
            : false,
        rangeHighlight:
          localStartDate && localEndDate
            ? isDateBeforeDate(currentDate, localEndDate) &&
              isDateAfterDate(currentDate, localStartDate)
            : false,
      });
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return d;
  }

  function formatDate(date) {
    return (
      monthNames[date.getMonth()] +
      " " +
      date.getDate() +
      ", " +
      date.getUTCFullYear()
    );
  }

  function setDates(dateString) {
    var d = new Date(dateString);

    if (localStartDate && allowRangeSelection) {
      if (
        d.getTime() == localStartDate.getTime() ||
        d.getTime() == localEndDate.getTime()
      ) {
        setLocalEndDate(d);
        setLocalStartDate(d);
      } else {
        if (
          d.getTime() - localStartDate.getTime() <
          localEndDate.getTime() - d.getTime()
        ) {
          setLocalStartDate(d);
        } else {
          setLocalEndDate(d);
        }
      }
    } else {
      setLocalStartDate(d);
      setLocalEndDate(d);
    }
  }

  function clearDates() {
    setLocalStartDate(null);
    setLocalEndDate(null);

    setStartDate("");
    setEndDate("");

    setDays(getDays(currentMonth + 1, currentYear));
  }

  return (
    <div className={classNames(styles.container)}>
      <img
        src={chevron}
        className={classNames(styles.arrow, styles.rightArrow)}
        onClick={() => incrementMonth()}
      />
      <img
        src={chevron}
        className={classNames(styles.arrow, styles.leftArrow)}
        onClick={() => decrementMonth()}
      />

      <div className={classNames(styles.monthYear, size == "small" ? styles.monthYear2 : "")}>
        {monthNames[currentMonth] + " " + currentYear}
      </div>

      <div className={classNames(size == "small" ? styles.grid2 :styles.grid)}>
        <div className={classNames(size == "small" ? styles.gridItem2 : styles.gridItem, styles.grayedOut)}>Mo</div>
        <div className={classNames(size == "small" ? styles.gridItem2 : styles.gridItem, styles.grayedOut)}>Tu</div>
        <div className={classNames(size == "small" ? styles.gridItem2 : styles.gridItem, styles.grayedOut)}>We</div>
        <div className={classNames(size == "small" ? styles.gridItem2 : styles.gridItem, styles.grayedOut)}>Th</div>
        <div className={classNames(size == "small" ? styles.gridItem2 : styles.gridItem, styles.grayedOut)}>Fr</div>
        <div className={classNames(size == "small" ? styles.gridItem2 : styles.gridItem, styles.grayedOut)}>Sa</div>
        <div className={classNames(size == "small" ? styles.gridItem2 : styles.gridItem, styles.grayedOut)}>Su</div>

        {days.map((day, index) => (
          <div
            key={index}
            className={classNames(
              size == "small" ? styles.gridItem2 : styles.gridItem,
              styles.days,
              day.grayedOut ? styles.grayedOut : "",
              day.disabled ? styles.disabled : "",
              day.highlighted ? (size == "small" ? styles.highlighted2 : styles.highlighted) : ""
            )}
            onClick={day.disabled ? null : () => setDates(day.dateString)}
          >
            {day.rangeHighlight ? (
              <div className={size == "small" ? styles.rangeHighlight2 : styles.rangeHighlight} />
            ) : (
              ""
            )}
            {day.day}
          </div>
        ))}
      </div>

      <button className={styles.button} onClick={() => clearDates()}>
        Clear
      </button>
    </div>
  );
}

DateRangePicker.propTypes = {
  startDateString: PropTypes.string,
  endDateString: PropTypes.string,
  setStartDate: PropTypes.func,
  setEndDate: PropTypes.func,
};
